import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Save, Monitor, ScanBarcode, Printer, Layout, Eye, Clock, Shield } from 'lucide-react';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

export interface KioskConfig {
  // Session & Security
  autoLogoutMinutes: number;
  requireReloginPerSession: boolean;
  // UI & Display
  showTemplateSelector: boolean;
  showPresetsTab: boolean;
  showSavedTemplatesTab: boolean;
  showImportButton: boolean;
  showBackToEditor: boolean;
  welcomeMessage: string;
  welcomeSubtext: string;
  // Print behavior
  defaultCopies: number;
  maxCopies: number;
  autoPrintOnLastField: boolean;
  clearAfterPrint: boolean;
  showPrintPreview: boolean;
  // Scanner
  enableBarcodeScanner: boolean;
  scannerAutoAdvance: boolean;
  // Template filtering
  allowedPresetIds: string[];
  allowedTemplateIds: string[];
  defaultTab: 'presets' | 'saved';
  // Branding
  headerTitle: string;
  footerText: string;
}

export const DEFAULT_KIOSK_CONFIG: KioskConfig = {
  autoLogoutMinutes: 0,
  requireReloginPerSession: false,
  showTemplateSelector: true,
  showPresetsTab: true,
  showSavedTemplatesTab: true,
  showImportButton: true,
  showBackToEditor: false,
  welcomeMessage: '',
  welcomeSubtext: '',
  defaultCopies: 1,
  maxCopies: 100,
  autoPrintOnLastField: false,
  clearAfterPrint: true,
  showPrintPreview: false,
  enableBarcodeScanner: true,
  scannerAutoAdvance: true,
  allowedPresetIds: [],
  allowedTemplateIds: [],
  defaultTab: 'presets',
  headerTitle: '',
  footerText: '',
};

interface PresetItem {
  id: string;
  name: string;
  is_active: boolean;
}

interface TemplateItem {
  id: string;
  name: string;
  is_global: boolean;
}

export const KioskSettings: React.FC = () => {
  const [config, setConfig] = useState<KioskConfig>(DEFAULT_KIOSK_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [presets, setPresets] = useState<PresetItem[]>([]);
  const [templates, setTemplates] = useState<TemplateItem[]>([]);

  useEffect(() => {
    const load = async () => {
      const [settingsRes, presetsRes, templatesRes] = await Promise.all([
        supabase.from('app_settings').select('value').eq('key', 'kiosk_config').maybeSingle(),
        supabase.from('template_presets').select('id, name, is_active').eq('is_active', true).order('sort_order'),
        supabase.from('templates').select('id, name, is_global').eq('is_global', true).order('name'),
      ]);

      if (settingsRes.data?.value && typeof settingsRes.data.value === 'object') {
        setConfig({ ...DEFAULT_KIOSK_CONFIG, ...(settingsRes.data.value as Record<string, unknown>) } as KioskConfig);
      }
      if (presetsRes.data) setPresets(presetsRes.data);
      if (templatesRes.data) setTemplates(templatesRes.data);
      setLoading(false);
    };
    load();
  }, []);

  const update = (patch: Partial<KioskConfig>) => {
    setConfig(prev => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: existing } = await supabase
        .from('app_settings')
        .select('id')
        .eq('key', 'kiosk_config')
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('app_settings')
          .update({ value: config as unknown as Json })
          .eq('key', 'kiosk_config');
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('app_settings')
          .insert({ key: 'kiosk_config', value: config as unknown as Json, description: 'Kiosk mode configuration' });
        if (error) throw error;
      }

      toast.success('Kiosk settings saved');
      setDirty(false);
    } catch (e) {
      toast.error('Failed to save kiosk settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      {/* Session & Security */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Session & Security
          </CardTitle>
          <CardDescription>Control kiosk session behavior and security</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="autoLogout">Auto-Logout Timer (minutes)</Label>
              <Input
                id="autoLogout"
                type="number"
                min={0}
                max={480}
                value={config.autoLogoutMinutes}
                onChange={(e) => update({ autoLogoutMinutes: parseInt(e.target.value) || 0 })}
              />
              <p className="text-xs text-muted-foreground">Sign out after inactivity. 0 = disabled.</p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium">Require Re-Login Per Session</Label>
              <p className="text-xs text-muted-foreground">
                Force users to re-authenticate each time they open kiosk mode
              </p>
            </div>
            <Switch
              checked={config.requireReloginPerSession}
              onCheckedChange={(v) => update({ requireReloginPerSession: v })}
            />
          </div>
        </CardContent>
      </Card>

      {/* UI & Display */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layout className="w-5 h-5 text-primary" />
            UI & Display
          </CardTitle>
          <CardDescription>Configure what users see in kiosk mode</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-4">
            <ToggleRow
              label="Show Template Selector"
              description="Allow users to switch between templates"
              checked={config.showTemplateSelector}
              onChange={(v) => update({ showTemplateSelector: v })}
            />
            <ToggleRow
              label="Show Presets Tab"
              description="Display the preset templates tab"
              checked={config.showPresetsTab}
              onChange={(v) => update({ showPresetsTab: v })}
            />
            <ToggleRow
              label="Show Saved Templates Tab"
              description="Display the user's saved templates tab"
              checked={config.showSavedTemplatesTab}
              onChange={(v) => update({ showSavedTemplatesTab: v })}
            />
            <ToggleRow
              label="Show Import Button"
              description="Allow importing JSON template files in kiosk"
              checked={config.showImportButton}
              onChange={(v) => update({ showImportButton: v })}
            />
            <ToggleRow
              label="Show Back to Editor Link"
              description="Display a link to return to the full editor"
              checked={config.showBackToEditor}
              onChange={(v) => update({ showBackToEditor: v })}
            />
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>Default Template Tab</Label>
            <Select
              value={config.defaultTab}
              onValueChange={(v) => update({ defaultTab: v as 'presets' | 'saved' })}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="presets">Presets</SelectItem>
                <SelectItem value="saved">Saved Templates</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>Welcome Message</Label>
            <Input
              value={config.welcomeMessage}
              onChange={(e) => update({ welcomeMessage: e.target.value })}
              placeholder="Select a Label Template"
            />
          </div>
          <div className="space-y-2">
            <Label>Welcome Subtext</Label>
            <Input
              value={config.welcomeSubtext}
              onChange={(e) => update({ welcomeSubtext: e.target.value })}
              placeholder="Choose a preset or your saved custom template"
            />
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>Header Title</Label>
            <Input
              value={config.headerTitle}
              onChange={(e) => update({ headerTitle: e.target.value })}
              placeholder="Kiosk Mode"
            />
            <p className="text-xs text-muted-foreground">Custom title shown in the kiosk header bar</p>
          </div>
          <div className="space-y-2">
            <Label>Footer Text</Label>
            <Input
              value={config.footerText}
              onChange={(e) => update({ footerText: e.target.value })}
              placeholder="Barcode scanner ready • Scan or select a template to begin"
            />
            <p className="text-xs text-muted-foreground">Custom text shown in the kiosk footer</p>
          </div>
        </CardContent>
      </Card>

      {/* Print Behavior */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-primary" />
            Print Behavior
          </CardTitle>
          <CardDescription>Configure printing defaults and automation</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Default Copies</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={config.defaultCopies}
                onChange={(e) => update({ defaultCopies: Math.max(1, parseInt(e.target.value) || 1) })}
              />
            </div>
            <div className="space-y-2">
              <Label>Max Copies</Label>
              <Input
                type="number"
                min={1}
                max={999}
                value={config.maxCopies}
                onChange={(e) => update({ maxCopies: Math.max(1, parseInt(e.target.value) || 100) })}
              />
            </div>
          </div>

          <div className="space-y-4">
            <ToggleRow
              label="Auto-Print on Last Field"
              description="Automatically trigger print when the last field is filled (via Enter or scan)"
              checked={config.autoPrintOnLastField}
              onChange={(v) => update({ autoPrintOnLastField: v })}
            />
            <ToggleRow
              label="Clear Fields After Print"
              description="Reset all fields and copies after printing"
              checked={config.clearAfterPrint}
              onChange={(v) => update({ clearAfterPrint: v })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Barcode Scanner */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ScanBarcode className="w-5 h-5 text-primary" />
            Barcode Scanner
          </CardTitle>
          <CardDescription>Configure barcode scanner integration</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleRow
            label="Enable Barcode Scanner"
            description="Listen for barcode scanner input to auto-fill fields"
            checked={config.enableBarcodeScanner}
            onChange={(v) => update({ enableBarcodeScanner: v })}
          />
          <ToggleRow
            label="Auto-Advance After Scan"
            description="Automatically move to the next field after a successful scan"
            checked={config.scannerAutoAdvance}
            onChange={(v) => update({ scannerAutoAdvance: v })}
          />
        </CardContent>
      </Card>

      {/* Template Filtering */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-primary" />
            Template Filtering
          </CardTitle>
          <CardDescription>
            Restrict which presets and templates are visible in kiosk mode. Leave empty to show all.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {presets.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Allowed Presets</Label>
              <p className="text-xs text-muted-foreground mb-2">
                {config.allowedPresetIds.length === 0 ? 'All presets shown' : `${config.allowedPresetIds.length} preset(s) selected`}
              </p>
              <ScrollArea className="max-h-40">
                <div className="grid grid-cols-2 gap-2 p-2 border rounded-lg bg-muted/30">
                  {presets.map(p => (
                    <div key={p.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`kiosk-preset-${p.id}`}
                        checked={config.allowedPresetIds.includes(p.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            update({ allowedPresetIds: [...config.allowedPresetIds, p.id] });
                          } else {
                            update({ allowedPresetIds: config.allowedPresetIds.filter(x => x !== p.id) });
                          }
                        }}
                      />
                      <label htmlFor={`kiosk-preset-${p.id}`} className="text-sm cursor-pointer">{p.name}</label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              {config.allowedPresetIds.length > 0 && (
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => update({ allowedPresetIds: [] })}>
                  Clear selection (show all)
                </Button>
              )}
            </div>
          )}

          {templates.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Allowed Global Templates</Label>
              <p className="text-xs text-muted-foreground mb-2">
                {config.allowedTemplateIds.length === 0 ? 'All global templates shown' : `${config.allowedTemplateIds.length} template(s) selected`}
              </p>
              <ScrollArea className="max-h-40">
                <div className="grid grid-cols-2 gap-2 p-2 border rounded-lg bg-muted/30">
                  {templates.map(t => (
                    <div key={t.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`kiosk-tpl-${t.id}`}
                        checked={config.allowedTemplateIds.includes(t.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            update({ allowedTemplateIds: [...config.allowedTemplateIds, t.id] });
                          } else {
                            update({ allowedTemplateIds: config.allowedTemplateIds.filter(x => x !== t.id) });
                          }
                        }}
                      />
                      <label htmlFor={`kiosk-tpl-${t.id}`} className="text-sm cursor-pointer">{t.name}</label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              {config.allowedTemplateIds.length > 0 && (
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => update({ allowedTemplateIds: [] })}>
                  Clear selection (show all)
                </Button>
              )}
            </div>
          )}

          {presets.length === 0 && templates.length === 0 && (
            <p className="text-sm text-muted-foreground italic">No presets or global templates available to filter</p>
          )}
        </CardContent>
      </Card>

      {/* Save button */}
      {dirty && (
        <div className="sticky bottom-4 z-10 flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="shadow-lg gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Kiosk Settings
          </Button>
        </div>
      )}
    </>
  );
};

// Reusable toggle row
const ToggleRow: React.FC<{
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, description, checked, onChange }) => (
  <div className="flex items-center justify-between">
    <div className="space-y-0.5">
      <Label className="text-sm font-medium">{label}</Label>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} />
  </div>
);
